/**
 * Session Types and Validation
 * 
 * This file provides a durable, typed Session model for the chat system.
 * All session-related frontend code should use these types and validation functions.
 */

export interface Session {
  id: string;
  userId: string;
  title: string;
  lastMessage?: string | null;
  createdAt: string;
  updatedAt: string;
  version: number;
}

export interface SessionMessage {
  id?: string;
  sessionId?: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt?: string;
}

export interface SessionWithMessages extends Session {
  messages: SessionMessage[];
}

/**
 * Error types for session operations
 */
export type SessionErrorType = 
  | 'network'      // Network/connection error
  | 'auth'         // Authentication/permission error
  | 'not_found'    // Session doesn't exist
  | 'validation'   // Data validation failed
  | 'unknown';     // Unknown error

export interface SessionError {
  type: SessionErrorType;
  message: string;
  retryable: boolean;
}

/**
 * Validate a session object has all required fields
 */
export function validateSession(data: unknown): { valid: boolean; session?: Session; error?: string } {
  if (!data || typeof data !== 'object') {
    return { valid: false, error: 'Invalid session data: not an object' };
  }

  const session = data as Record<string, unknown>;

  if (typeof session.id !== 'string' || !session.id) {
    return { valid: false, error: 'Invalid session: missing or invalid id' };
  }

  if (typeof session.title !== 'string') {
    return { valid: false, error: 'Invalid session: missing or invalid title' };
  }

  // Normalize timestamps
  const createdAt = session.createdAt || session.created_at;
  const updatedAt = session.updatedAt || session.updated_at;

  if (!createdAt) {
    return { valid: false, error: 'Invalid session: missing createdAt' };
  }

  const validSession: Session = {
    id: session.id as string,
    userId: (session.userId || session.user_id || '') as string,
    title: session.title as string,
    lastMessage: (session.lastMessage || session.last_message || null) as string | null,
    createdAt: String(createdAt),
    updatedAt: String(updatedAt || createdAt),
    version: typeof session.version === 'number' ? session.version : 1,
  };

  return { valid: true, session: validSession };
}

/**
 * Validate a session message object.
 * Only role and content are strictly required - other fields are optional
 * to match the actual API response which may not include all fields.
 */
export function validateSessionMessage(data: unknown): { valid: boolean; message?: SessionMessage; error?: string } {
  if (!data || typeof data !== 'object') {
    return { valid: false, error: 'Invalid message data: not an object' };
  }

  const msg = data as Record<string, unknown>;

  // Only role and content are strictly required
  if (msg.role !== 'user' && msg.role !== 'assistant') {
    return { valid: false, error: 'Invalid message: role must be "user" or "assistant"' };
  }

  if (typeof msg.content !== 'string') {
    return { valid: false, error: 'Invalid message: missing or invalid content' };
  }

  const createdAt = msg.createdAt || msg.created_at;

  const validMessage: SessionMessage = {
    id: typeof msg.id === 'string' ? msg.id : undefined,
    sessionId: (msg.sessionId || msg.session_id) as string | undefined,
    role: msg.role as 'user' | 'assistant',
    content: msg.content as string,
    createdAt: createdAt ? String(createdAt) : undefined,
  };

  return { valid: true, message: validMessage };
}

/**
 * Validate an array of session messages
 */
export function validateSessionMessages(data: unknown[]): { valid: boolean; messages: SessionMessage[]; errors: string[] } {
  const messages: SessionMessage[] = [];
  const errors: string[] = [];

  for (let i = 0; i < data.length; i++) {
    const result = validateSessionMessage(data[i]);
    if (result.valid && result.message) {
      messages.push(result.message);
    } else {
      errors.push(`Message ${i}: ${result.error}`);
    }
  }

  return {
    valid: errors.length === 0,
    messages,
    errors,
  };
}

/**
 * Categorize an API error into a SessionError
 */
export function categorizeError(error: unknown, response?: Response): SessionError {
  // Network errors (fetch failed)
  if (error instanceof TypeError && (error.message.includes('fetch') || error.message.includes('network'))) {
    return {
      type: 'network',
      message: 'Unable to connect. Please check your internet connection.',
      retryable: true,
    };
  }

  // HTTP response errors
  if (response) {
    if (response.status === 401 || response.status === 403) {
      return {
        type: 'auth',
        message: 'Please sign in to access your sessions.',
        retryable: false,
      };
    }

    if (response.status === 404) {
      return {
        type: 'not_found',
        message: 'This session could not be found. It may have been deleted.',
        retryable: false,
      };
    }

    if (response.status >= 500) {
      return {
        type: 'network',
        message: 'Server error. Please try again in a moment.',
        retryable: true,
      };
    }
  }

  // Default unknown error
  console.error('[categorizeError] Unknown error:', error);
  return {
    type: 'unknown',
    message: 'An unexpected error occurred. Please try again.',
    retryable: true,
  };
}

/**
 * Export a session to JSON format for backup.
 * Handles optional fields gracefully.
 */
export function exportSessionToJSON(session: Session, messages: SessionMessage[]): string {
  const exportData = {
    exportedAt: new Date().toISOString(),
    exportVersion: 1,
    session: {
      id: session.id,
      title: session.title,
      createdAt: session.createdAt,
      updatedAt: session.updatedAt,
      version: session.version,
    },
    messages: messages.map((m, idx) => ({
      index: idx,
      role: m.role,
      content: m.content,
      ...(m.createdAt && { createdAt: m.createdAt }),
    })),
  };

  return JSON.stringify(exportData, null, 2);
}

/**
 * Format a timestamp for display
 */
export function formatSessionTimestamp(timestamp: string): string {
  try {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  } catch {
    return '';
  }
}
